import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, LayoutGrid, PenLine, Sparkles, X } from 'lucide-react';
import {
  buildMultiAnswer,
  canAnswerOther,
  fieldLabel,
  formatFieldValue,
  initialChoicePicks,
  isMultiChoice,
  optionsHaveImages,
  readField,
  splitInlineOptions,
} from '@/lib/creationPlan';
import { TwinneeLogo } from '@/components/TwinneeLogo';

const INLINE_IMAGE_OPTIONS = 8;
const INLINE_TEXT_OPTIONS = 12;

function OptionThumb({ opt, className = '' }) {
  const [failed, setFailed] = useState(false);
  if (!opt.image || failed) {
    return (
      <span className={`${className} shrink-0 bg-gradient-to-br from-[#2DD4BF]/30 to-[#7DD3FC]/20 flex items-center justify-center`}>
        <TwinneeLogo className="w-3 h-3 text-[#2DD4BF]" />
      </span>
    );
  }
  return (
    <img
      src={opt.image}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className={`${className} shrink-0 object-cover bg-white/5`}
    />
  );
}

function currentFieldValue(plan, path) {
  if (path === 'visual.style') {
    return (
      plan?.input?.visual?.style_notes
      || plan?.input?.image_style_notes
      || readField(plan?.input, path)
      || ''
    );
  }
  return readField(plan?.input, path);
}

export function fieldQuestionFor(plan, path) {
  return (
    plan?.field_questions?.[path] || {
      field: path,
      prompt: `Update ${fieldLabel(path)}`,
      type: 'text',
      options: [],
      allow_custom: true,
      placeholder: '',
      multiple: false,
    }
  );
}

export function PlanFieldTiles({
  paths,
  plan,
  missing,
  optionalPending,
  busy,
  onEdit,
}) {
  const missingSet = missing instanceof Set ? missing : new Set(missing || []);
  const optionalSet = optionalPending instanceof Set ? optionalPending : new Set(optionalPending || []);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
      {paths.map((path) => {
        const pending = missingSet.has(path);
        const optional = !pending && optionalSet.has(path);
        const label =
          plan?.flow?.id === 'reels_shorts' && path === 'visual.style' ? 'B-ROLL look' : fieldLabel(path);
        return (
          <button
            key={path}
            type="button"
            disabled={busy}
            onClick={() => onEdit(path)}
            data-testid={`plan-field-${path}`}
            title={`Edit ${label}`}
            className={`rounded-xl border px-3 py-2 text-left transition-colors cursor-pointer hover:border-[#2DD4BF]/50 hover:bg-white/[0.05] ${
              pending
                ? 'border-amber-400/40 bg-amber-400/[0.06]'
                : optional
                  ? 'border-dashed border-white/15 bg-white/[0.02]'
                  : 'border-white/10 bg-white/[0.03]'
            } disabled:opacity-60`}
          >
            <p className="text-[10px] uppercase tracking-wide text-[#6B7280] flex items-center gap-1">
              {pending || optional ? (
                <span className={`inline-block w-2 h-2 rounded-full ${pending ? 'bg-amber-300' : 'bg-[#6B7280]'}`} />
              ) : (
                <Check className="w-2.5 h-2.5 text-[#2DD4BF]" />
              )}
              {label}
              <PenLine className="w-2.5 h-2.5 ml-auto text-[#6B7280]" />
            </p>
            <p className="text-white font-medium truncate mt-0.5 capitalize">
              {pending
                ? 'Pending'
                : optional
                  ? 'Optional · to ask'
                  : formatFieldValue(path, currentFieldValue(plan, path))}
            </p>
          </button>
        );
      })}
    </div>
  );
}

export function PlanFieldEditorDialog({ plan, path, locked, busy, onSave, onClose, onRegenerateScript }) {
  const question = fieldQuestionFor(plan, path);
  const current = currentFieldValue(plan, path);
  const multiChoice = isMultiChoice(question);
  const otherAllowed = canAnswerOther(question);
  const imageOptions = optionsHaveImages(question);
  const [picks, setPicks] = useState(() =>
    multiChoice
      ? (Array.isArray(current) ? current.map(String) : initialChoicePicks(question))
      : [],
  );
  const [text, setText] = useState(() => {
    if (multiChoice) return '';
    if (Array.isArray(current)) return current.join(', ');
    return current == null || current === '' ? '' : String(current);
  });
  const [otherOpen, setOtherOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [regenPrompt, setRegenPrompt] = useState('');
  const [regenOpen, setRegenOpen] = useState(false);
  const isScript = path === 'script';
  const canRegenScript = isScript && typeof onRegenerateScript === 'function' && !locked;

  useEffect(() => {
    if (!question) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [question, onClose]);

  const showsSamples = (question?.options || []).some((opt) => opt.sample);
  const showEveryFormat = path === 'flow_params.story_format' || showsSamples;
  const { inline, hidden } = useMemo(
    () =>
      splitInlineOptions(question?.options, {
        limit: showAll || showEveryFormat ? 999 : imageOptions ? INLINE_IMAGE_OPTIONS : INLINE_TEXT_OPTIONS,
        picks,
      }),
    [question?.options, showAll, showEveryFormat, imageOptions, picks],
  );

  if (!question) return null;

  const selectedValue = (opt) => (multiChoice ? picks.includes(opt.value) : String(text) === String(opt.value));
  const longText = question.type === 'text' || path === 'visual.style' || (typeof current === 'string' && String(current).length > 80);

  const submit = () => {
    if (locked || busy) return;
    if (multiChoice) {
      const { value, label } = buildMultiAnswer(question, picks, otherOpen ? text : '');
      if (!value.length) return;
      onSave(path, value, label);
      return;
    }
    const chosen = (question.options || []).find((o) => String(o.value) === String(text));
    const value = String(text || '').trim();
    if (!value) return;
    onSave(path, value, chosen?.label || value);
  };

  const pickOption = (opt) => {
    if (locked) return;
    if (multiChoice) {
      setPicks((prev) => (prev.includes(opt.value) ? prev.filter((v) => v !== opt.value) : [...prev, opt.value]));
      return;
    }
    onSave(path, opt.value, opt.label);
  };

  const title = fieldLabel(path);

  return createPortal(
    <div
      className="fixed inset-0 z-[205] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
      role="presentation"
      onClick={onClose}
      data-testid="plan-field-editor"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={question.prompt || title}
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${imageOptions ? 'max-w-3xl' : 'max-w-lg'} max-h-[100dvh] sm:max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-white/10 bg-[#0b0e14] shadow-2xl`}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-white/10">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white">{title}</p>
            <p className="text-[11px] text-[#9CA3AF]">{question.prompt}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-white hover:bg-white/10 cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className={`flex-1 min-h-0 overflow-y-auto p-5 ${imageOptions ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3' : 'space-y-3'}`}>
          {(question.options || []).length
            ? (showAll || showEveryFormat ? question.options : inline).map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  disabled={locked}
                  onClick={() => pickOption(opt)}
                  data-testid="plan-field-option"
                  className={`${
                    showsSamples
                      ? 'w-full rounded-xl border text-left px-3 py-2.5 block'
                      : imageOptions
                      ? 'flex flex-col rounded-xl border text-left overflow-hidden'
                      : 'px-3 py-1.5 rounded-full text-xs border inline-flex items-center gap-1.5 mr-1.5 mb-1.5'
                  } cursor-pointer transition-colors disabled:opacity-50 ${
                    selectedValue(opt)
                      ? 'border-[#2DD4BF] bg-[#2DD4BF]/15 text-[#2DD4BF]'
                      : 'border-white/15 text-[#E5E7EB] hover:border-[#2DD4BF]/50'
                  }`}
                >
                  {imageOptions ? <OptionThumb opt={opt} className="w-full aspect-square" /> : null}
                  <span className={imageOptions ? 'px-2.5 py-2 text-xs text-white leading-snug' : 'text-xs font-semibold text-white'}>
                    {opt.label}
                    {opt.isDefault ? (
                      <span className="ml-1.5 text-[9px] uppercase tracking-wide text-[#2DD4BF]">current</span>
                    ) : null}
                    {opt.recommended && !opt.isDefault ? (
                      <span className="ml-1.5 text-[9px] uppercase tracking-wide text-[#5EEAD4]">suggested</span>
                    ) : null}
                  </span>
                  {opt.sample ? (
                    <pre
                      data-testid="plan-field-format-sample"
                      className="mt-2 whitespace-pre-wrap font-sans text-[11px] leading-relaxed text-[#D1D5DB] max-h-28 overflow-y-auto"
                    >
                      {opt.sample}
                    </pre>
                  ) : null}
                </button>
              ))
            : null}
          {!imageOptions && hidden ? (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="px-3 py-1.5 rounded-full text-xs cursor-pointer border border-white/20 text-[#E5E7EB] inline-flex items-center gap-1"
            >
              <LayoutGrid className="w-3 h-3" /> More · +{hidden}
            </button>
          ) : null}
          {otherAllowed && !multiChoice ? (
            <button
              type="button"
              aria-pressed={otherOpen || (text && !(question.options || []).some((o) => String(o.value) === String(text)))}
              onClick={() => setOtherOpen(true)}
              className={`px-3 py-1.5 rounded-full text-xs cursor-pointer border border-dashed inline-flex items-center gap-1 ${
                otherOpen ? 'border-[#2DD4BF]/70 bg-[#2DD4BF]/15 text-[#2DD4BF]' : 'border-white/20 text-[#E5E7EB]'
              }`}
            >
              <PenLine className="w-3 h-3" /> Other
            </button>
          ) : null}
          {question.type === 'text' || otherOpen || longText || multiChoice ? (
            longText && !multiChoice ? (
              <textarea
                rows={5}
                value={text}
                disabled={locked}
                onChange={(e) => setText(e.target.value)}
                placeholder={question.placeholder || ''}
                data-testid="plan-field-text"
                className="w-full rounded-xl bg-white/[0.04] border border-white/15 px-3 py-2 text-xs text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#2DD4BF]/60 resize-y"
              />
            ) : (
              <input
                type="text"
                value={text}
                disabled={locked}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submit();
                  }
                }}
                placeholder={question.placeholder || (multiChoice ? 'Type your own — separate with commas' : 'Type your own')}
                data-testid="plan-field-text"
                className="w-full rounded-xl bg-white/[0.04] border border-white/15 px-3 py-2 text-xs text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#2DD4BF]/60"
              />
            )
          ) : null}
        </div>
        {canRegenScript ? (
          <div className="px-5 py-3 border-t border-white/10 space-y-2" data-testid="plan-field-script-regen">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => onRegenerateScript('')}
                className="px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer border border-[#A78BFA]/50 bg-[#A78BFA]/10 text-[#C4B5FD] hover:bg-[#A78BFA]/20 disabled:opacity-50 inline-flex items-center gap-1"
              >
                <Sparkles className="w-3 h-3" /> Regenerate with AI
              </button>
              <button
                type="button"
                disabled={busy}
                aria-expanded={regenOpen}
                onClick={() => setRegenOpen((open) => !open)}
                className={`px-3 py-1.5 rounded-full text-[11px] cursor-pointer border inline-flex items-center gap-1 disabled:opacity-50 ${
                  regenOpen
                    ? 'border-[#A78BFA] bg-[#A78BFA]/20 text-[#DDD6FE]'
                    : 'border-white/15 text-[#E5E7EB] hover:border-[#A78BFA]/40'
                }`}
              >
                <PenLine className="w-3 h-3" /> New prompt
              </button>
            </div>
            {regenOpen ? (
              <div className="space-y-2">
                <textarea
                  rows={2}
                  maxLength={2000}
                  value={regenPrompt}
                  onChange={(e) => setRegenPrompt(e.target.value)}
                  placeholder="e.g. Make it funnier, shorter, stronger ending"
                  data-testid="plan-field-script-regen-prompt"
                  className="w-full rounded-xl bg-white/[0.04] border border-white/15 px-3 py-2 text-xs text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#A78BFA]/60 resize-y"
                />
                <button
                  type="button"
                  disabled={busy || !regenPrompt.trim()}
                  onClick={() => onRegenerateScript(regenPrompt.trim())}
                  data-testid="plan-field-script-regen-submit"
                  className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-[#A78BFA] text-[#0B0718] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Regenerate with prompt
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="px-5 py-3 border-t border-white/10 flex items-center gap-2">
          {locked ? (
            <p className="text-[11px] text-amber-300/90">Story already started — start a new plan to change this.</p>
          ) : path === 'visual.style' ? (
            <p className="text-[11px] text-[#9CA3AF] flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-[#2DD4BF]" /> Catalog look or paste a custom style note
            </p>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-[11px] text-[#9CA3AF] hover:text-white cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={locked || busy}
            data-testid="plan-field-save"
            className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-[#2DD4BF] text-[#06080D] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
